import { format } from "date-fns";
import { formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";
import { GlossDateAbstraction } from "@bitwarden/web-vault/app/models/data/shared/gloss-date/gloss-date.abstraction";
import { GlossDate } from "@bitwarden/web-vault/app/models/data/shared/gloss-date/gloss-date";
import { GlossDateTime } from "@bitwarden/web-vault/app/models/data/shared/gloss-date/gloss-date-time";
import {
  DateAsISOStringType,
  GlossDateAsObjectType,
} from "@bitwarden/web-vault/app/models/store/types/store.types";

export class GlossDateFactory {
  // Use this function when converting from data stored in DALI to the model
  static createGlossDateFromObject(obj: GlossDateAsObjectType): GlossDateAbstraction {
    if (obj?.time) {
      const glossDateTime = new GlossDateTime();
      glossDateTime.setFromObject(obj);
      return glossDateTime;
    } else {
      const glossDate = new GlossDate();
      glossDate.setFromObject(obj);
      return glossDate;
    }
  }

  /**
   * Use this function when importing with an iso date string and a timezone
   * Can specify whether to store the time or not in DALI
   *
   * @param isoDateString - Standard iso date string. Same as returned from javascript Date.prototype.toISOString()
   *                        YYYY-MM-DDTHH:mm:ss.sssZ or ±YYYYYY-MM-DDTHH:mm:ss.sssZ
   * @param storeTime - tell the factory whether to store the time part of the iso date or ignore it
   * @param timeZone - provide a timezone different from the local timezone in the browser
   */
  static createFromISOString(
    isoDateString: DateAsISOStringType,
    storeTime = false,
    timeZone?: string,
  ): GlossDateAbstraction {
    if (storeTime) {
      const glossDateTime = new GlossDateTime();
      glossDateTime.setFromIsoString(isoDateString, timeZone);
      return glossDateTime;
    } else {
      const glossDate = new GlossDate();
      glossDate.setFromIsoString(isoDateString, timeZone);
      return glossDate;
    }
  }

  /**
   * Use this function when creating a date from a string like from a manual form
   *
   * @param dateString - should be in the format YYYY-MM-DD, e.g. 2023-10-01,
   *                     but will accept anything that javascript Date() will accept
   * @param time - supply the time if you have it and want to store it in format HH:mm:ss
   * @param timeZone - supply a timezone if your date is not in the local timezone
   * @param reliableTimeSource - indicates if the time source is reliable, true if the
   *                             time is accurately usable, false otherwise
   */
  static createFromDateString(
    dateString: string,
    time?: string,
    timeZone?: string,
    reliableTimeSource = false,
  ): GlossDateAbstraction {
    let date: Date;
    if (time) {
      if (!timeZone) {
        date = new Date(`${dateString}T${time}`);
      } else {
        date = zonedTimeToUtc(`${dateString}T${time}`, timeZone);
      }
    } else {
      date = zonedTimeToUtc(
        `${dateString}T00:00:00`,
        Intl.DateTimeFormat().resolvedOptions().timeZone,
      );
    }

    const dateAsObj: GlossDateAsObjectType = {
      date: date.toISOString(),
      time: time,
      timeZone: timeZone,
      reliableTimeSource: reliableTimeSource, // indicates if the time source is reliable
    };
    if (time) {
      const glossDateTime = new GlossDateTime();
      glossDateTime.setFromObject(dateAsObj, reliableTimeSource);
      return glossDateTime;
    } else {
      const glossDate = new GlossDate();
      glossDate.setFromObject(dateAsObj);
      return glossDate;
    }
  }

  /**
   * Use this function when creating a date from a javascript Date object
   *
   * @param date - a javascript Date object
   * @param reliableTimeSource - indicates if the time source is reliable
   * @param time - supply the time in format HH:mm:ss if you want to enforce a specific time
   * @param timeZone - supply a timezone if your date is not in the local timezone
   */
  static createFromDate(
    date: Date,
    reliableTimeSource = false,
    time?: string,
    timeZone?: string,
  ): GlossDateAbstraction {
    if (time) {
      let dateWithTime: Date;
      if (timeZone) {
        const dateString = formatInTimeZone(date, timeZone, "yyyy-MM-dd");
        dateWithTime = zonedTimeToUtc(`${dateString}T${time}`, timeZone);
      } else {
        const dateString = format(date, "yyyy-MM-dd");
        dateWithTime = zonedTimeToUtc(
          `${dateString}T${time}`,
          Intl.DateTimeFormat().resolvedOptions().timeZone,
        );
      }
      const dateAsObj: GlossDateAsObjectType = {
        date: dateWithTime.toISOString(), // always in UTC
        time: time, // time can be null if not known
        timeZone: timeZone, // will assume that the date is in the local timezone if not provided
        reliableTimeSource: reliableTimeSource, // indicates if the time source is reliable
      };
      const glossDateTime = new GlossDateTime();
      glossDateTime.setFromObject(dateAsObj, false);
      return glossDateTime;
    } else {
      const dateAsObj: GlossDateAsObjectType = {
        date: date.toISOString(), // always in UTC
        time: null, // time can be null if not known
        timeZone: null, // will assume that the date is in the local timezone if not provided
        reliableTimeSource: false, // indicates if the time source is reliable
      };

      const glossDate = new GlossDate();
      glossDate.setFromObject(dateAsObj);
      return glossDate;
    }
  }

  /**
   * Use this function when creating a GlossDate from an existing GlossDate object. This is used in
   * balance alignment to create a new GlossDate from another transaction's GlossDate.
   *
   * @param glossDate - an existing GlossDateAbstraction object, either GlossDate or GlossDateTime that you want to copy
   */
  static createFromGlossDate(glossDate: GlossDateAbstraction): GlossDateAbstraction {
    if (glossDate instanceof GlossDateTime) {
      const newGlossDateTime = new GlossDateTime();
      newGlossDateTime.setFromObject(glossDate);
      return newGlossDateTime;
    } else {
      const newGlossDate = new GlossDate();
      newGlossDate.setFromObject({ ...glossDate, time: null, reliableTimeSource: false });
      return newGlossDate;
    }
  }

  /**
   * Use this function when creating from an object that was once a GlossDateAbstraction.
   * This might be when we have passed a GlossDateAbstraction object through a web worker so that the original class
   * methods are not available.
   *
   * @param obj - needs to have once been a GlossDateAbstraction object, e.g. GlossDate or GlossDateTime.
   */
  static createFromAnyObject(obj: any): GlossDateAbstraction {
    const dateAsObj: GlossDateAsObjectType = {
      date: obj?.date || new Date().toISOString(), // default to now if no date is provided
      timeZone: obj?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone, // default to local timezone if no timezone is provided
      time: obj?.time || null, // time can be null if not known
      reliableTimeSource: obj?.reliableTimeSource || false, // default to false if not provided
    };
    return this.createGlossDateFromObject(dateAsObj);
  }
}
